import { useState } from 'react';

const useStimulsoftPrint = () => {
  const [showViewer, setShowViewer] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);

  // Load Stimulsoft scripts dynamically
  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => {
        console.log(`${src} loaded`);
        resolve();
      };
      script.onerror = (e) => {
        console.error(`Failed to load script ${src}`, e);
        reject(new Error(`Failed to load script: ${src}`));
      };
      document.body.appendChild(script);
    });
  };

  // Initialize Stimulsoft with license
  const waitForStimulsoft = async () => {
    if (window.Stimulsoft?.Viewer) {
      console.log('Stimulsoft already loaded');
      return window.Stimulsoft;
    }

    console.log('Loading Stimulsoft scripts...');
    await loadScript('/stimulsoft/stimulsoft.reports.js');
    await loadScript('/stimulsoft/stimulsoft.viewer.js');

    if (!window.Stimulsoft?.Viewer) {
      throw new Error('Stimulsoft failed to load properly.');
    }

    window.Stimulsoft.Base.StiLicense.key = "6vJhGtLLLz2GNviWmUTrhSqnOItdDwjBylQzQcAOiHk8RHJ6YukLSxFNxOsGywCmSfDmNx54ijoADYy8ZbVyM2jfqO" +
      "XKdON4rOS1c4pYt+vz3OZj2h5NQ5xt1EHgsdxT9FLudqeHLONw2Ilz5GFCADtzxp1LJgZzdOKAPwaCt2pKcALM8JCu" +
      "oSWTXhL6pSipVdAC3kmr4l/bdrNhEmf4ARCRcv8Je5e5c2/Jjol0t8ey/O5HrlXlz+EC2Cz4Dx66o6WvwzDMBv56zd" +
      "3cSiUMqyw5J9NVd3Dkk09nWGDipivUFGdO6p0xGmG+mXDOC+aap+URaMm3DKj2kWRXguqTyadS7r718J6UNqnuH5fG" +
      "/U0fnt5FumYLZmz+sPlmEo4tu3tDnAhGKcDj+ZIMG6h2YPo49FKTI/xrGWxUpVdA2z/1XqMN/LzY6eVUxOfLMuFNsZ" +
      "9anqLes7Xr5v3I2YWIfLeq60bMG0UbFYV0s7C7ybmH9Kvdnt7ddUbP0CWtZLNnbDxbRMayRP9ntwdQibsT5x6wJKA2" +
      "NPA7vDQwiKBmPVzZpBABwAAjeeztmBdxD/Rd";

    console.log('Stimulsoft loaded and license applied.');
    return window.Stimulsoft;
  };

  const waitForContainer = (containerId, retries = 40, interval = 25) =>
    new Promise((resolve, reject) => {
      const attempt = (n) => {
        const el = document.getElementById(containerId);
        if (el) return resolve(el);
        if (n <= 0) return reject(new Error(`Container not found: ${containerId}`));
        setTimeout(() => attempt(n - 1), interval);
      };
      attempt(retries);
    });

  const getReportPayload = (apiResponse) => {
    const queue = [{ value: apiResponse, depth: 0 }];
    const visited = new WeakSet();
    const observedKeys = new Set();
    const wrapperKeys = new Set(['data', 'result', 'value', 'payload', 'report', 'reportdata']);

    while (queue.length > 0) {
      const { value, depth } = queue.shift();
      let current = value;

      // ASP.NET string actions can result in one or more JSON-encoded layers.
      for (let parseDepth = 0; typeof current === 'string' && parseDepth < 3; parseDepth += 1) {
        try {
          current = JSON.parse(current);
        } catch {
          break;
        }
      }

      if (!current || typeof current !== 'object' || visited.has(current)) {
        continue;
      }
      visited.add(current);

      const keys = Object.keys(current);
      keys.forEach((key) => observedKeys.add(key));
      const isRenderedDocument = keys.some((key) => key.toLowerCase() === 'renderedpages')
        || keys.some((key) => key.toLowerCase() === 'renderedwith');

      if (isRenderedDocument) {
        return { renderedDocument: current };
      }

      const templateKey = keys.find((key) => {
        const normalizedKey = key.toLowerCase();
        return normalizedKey === 'template' || normalizedKey === 'reporttemplate';
      });

      if (templateKey) {
        const dataKey = keys.find((key) => {
          const normalizedKey = key.toLowerCase();
          return normalizedKey === 'data'
            || normalizedKey === 'reportdata'
            || normalizedKey === 'datasources'
            || normalizedKey === 'jsondata';
        });

        return {
          template: current[templateKey],
          data: dataKey ? current[dataKey] : {}
        };
      }

      if (depth < 5) {
        keys.forEach((key) => {
          if (wrapperKeys.has(key.toLowerCase())) {
            queue.push({ value: current[key], depth: depth + 1 });
          }
        });
      }
    }

    const responseKeys = apiResponse && typeof apiResponse === 'object'
      ? Object.keys(apiResponse).join(', ')
      : typeof apiResponse;
    const dataKeys = apiResponse?.data && typeof apiResponse.data === 'object'
      ? Object.keys(apiResponse.data).join(', ')
      : typeof apiResponse?.data;

    throw new Error(
      `The report response does not contain a template. Response keys: ${responseKeys || 'none'}; `
      + `data keys: ${dataKeys || 'none'}; discovered keys: `
      + `${Array.from(observedKeys).slice(0, 25).join(', ') || 'none'}.`
    );
  };

  // Load the report template and bind the API data in the browser.
  const renderStimulsoftViewer = async (apiResponse, containerId = 'stimulsoftViewerContent') => {
    try {
      const Stimulsoft = await waitForStimulsoft();
      const payload = getReportPayload(apiResponse);

      const report = new Stimulsoft.Report.StiReport();
      const isRenderedDocument = Boolean(payload.renderedDocument);

      if (isRenderedDocument) {
        report.loadDocument(payload.renderedDocument);
      } else {
        report.load(payload.template);

        // Parameters are supplied by the API, so do not prompt for them again.
        for (let i = 0; i < report.dictionary.variables.count; i += 1) {
          report.dictionary.variables.getByIndex(i).requestFromUser = false;
        }

        report.dictionary.databases.clear();
        report.dictionary.dataSources.clear();

        const dataSet = new Stimulsoft.System.Data.DataSet();
        dataSet.readJson(payload.data || {});

        report.regData('JsonData', 'JsonData', dataSet);
        report.dictionary.synchronize();
      }

      const options = new Stimulsoft.Viewer.StiViewerOptions();
      options.appearance.showBranding = false;
      options.appearance.showAboutButton = false;
      options.appearance.fullScreenMode = false;
      options.appearance.scrollbarsMode = true;
      options.appearance.zoom = Stimulsoft.Viewer.StiZoomMode.PageWidth;
      options.toolbar.showDesignButton = false;
      options.toolbar.showPrintButton = true;
      options.toolbar.showFullScreenButton = true;
      options.toolbar.showParametersButton = false;
      options.exports.showExportDialog = true;
      // Remove height/width constraints to let the viewer size itself
      options.height = "100%";
      options.width = "100%";
      const viewer = new Stimulsoft.Viewer.StiViewer(options, 'StiViewer', false);

      // Clear the container first
      const container = document.getElementById(containerId);

      if (!container) {
        console.error('Container not found with ID:', containerId);
        return;
      }
      container.innerHTML = '';

      if (isRenderedDocument) {
        viewer.report = report;
        viewer.renderHtml(container);
      } else {
        await new Promise((resolve) => {
          report.renderAsync(() => {
            viewer.report = report;
            viewer.renderHtml(container);
            resolve();
          });
        });
      }

      setTimeout(() => {
        const viewerContainer = container; // already the element we used
        if (viewerContainer) {
          const viewerDiv = viewerContainer.querySelector('div');
          if (viewerDiv) {
            viewerDiv.style.height = '100%';
            viewerDiv.style.width = '100%';
            viewerDiv.style.overflow = 'auto';
          }
        }

        if (viewer.jsObject && viewer.jsObject.options) {
          viewer.jsObject.options.appearance.scrollbarsMode = true;
          viewer.jsObject.options.appearance.zoom = Stimulsoft.Viewer.StiZoomMode.PageWidth;
        }
      }, 300);

      console.log('renderStimulsoftViewer completed successfully');
    } catch (error) {
      console.error('Error in renderStimulsoftViewer:', error);
      throw error;
    }
  };

  // Generic print handler  
  const handlePrint = async (printServiceFunction, ...params) => {
    // last param may be the containerId if provided by PrintButton
    const maybeId = params[params.length - 1];
    const containerId = typeof maybeId === 'string' && maybeId.startsWith('stimulsoftViewerContent')
      ? params.pop()
      : 'stimulsoftViewerContent';

    setPrintLoading(true);
    try {
      const reportData = await printServiceFunction(...params);

      console.log("Axios status:", reportData.status);
      console.log("Axios content-type:", reportData.headers?.["content-type"]);
      console.log("Axios data:", reportData.data);

      console.log('handlePrint - Full reportData response:', reportData);
      console.log('handlePrint - reportData.data:', reportData.data);
      console.log('handlePrint - reportData.data.data:', reportData.data?.data);

      setShowViewer(true);
      // wait for container to mount via Portal before rendering
      await waitForContainer(containerId).catch((e) => console.warn(e.message));

      if (!reportData) {
        throw new Error('No valid report data found in response');
      }

      await renderStimulsoftViewer(reportData, containerId);
    } catch (error) {
      console.error('Error generating print report:', error);
    } finally {
      setPrintLoading(false);
    }
  };

  // Close viewer and cleanup
  const closeViewer = () => {
    setShowViewer(false);
    const viewerElement = document.getElementById('stimulsoftViewerContent');
    if (viewerElement) viewerElement.innerHTML = '';
  };

  return {
    showViewer,
    setShowViewer,
    printLoading,
    handlePrint,
    closeViewer,
    renderStimulsoftViewer
  };
};

export default useStimulsoftPrint;
